import {ThemeProvider} from '@mui/material'
import {createTheme} from '@mui/material/styles'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {AppStateProvider} from 'components/Context'
import {useFirebase, useSession} from 'components/Firebase/hooks'
import WebsiteBanner from 'components/WebsiteBanner'
import {NOTIFICATION_NOT_GRANTED_MESSAGE} from 'components/constants'
import {displayNotificationBanner} from 'components/utils'
import {registerAppCheckTokenGetter} from 'lib/api/appCheck'
import {usePostgreSQL} from 'lib/api/featureFlag'
import {notificationsApi} from 'lib/api'
import {setBrowserPushPrincipal} from 'lib/push/browserPush'
import {clearPendingAdminSubmission} from 'components/RoleManagementAccess/pendingSubmission'
import {lazy, Suspense, useEffect, useRef, useState} from 'react'
import {Navigate, Route, Routes, useLocation} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './globals.scss'

const Accounts = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ './components/Account')
)
const AgreementDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/AgreementDetailPage'
    )
)
const AgreementPage = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/AgreementPage')
)
const AdminEmailVerification = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/AdminEmailVerification'
    )
)
const AuditLogPage = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/AuditLogPage')
)
const BankAccountInformation = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/BankAccountInformation'
    )
)
const Categories = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/Categories')
)
const ChangePassword = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/ChangePassword')
)
const ContactDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ './components/ContactDetailsPage'
    )
)
const ContactPage = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ './components/ContactPage')
)
const ContentPages = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ './components/ContentPages')
)
const CreateNewAccount = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ './components/CreateNewAccount'
    )
)
const CreateNewCategory = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/CreateNewCategory')
)
const CreateNewContact = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ './components/CreateNewContact'
    )
)
const CreateNewInvoice = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/CreateNewInvoice')
)
const CreateNewSector = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/CreateNewSector')
)
const Dashboard = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ './components/Dashboard')
)
const DealDetailsPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/DealDetailsPage')
)
const DealPage = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/DealPage')
)
const EquipmentReportDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/EquipmentReportDetailPage'
    )
)
const EquipmentReportSettingsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/EquipmentReportSettingsPage'
    )
)
const EquipmentReportTable = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/EquipmentReportTable'
    )
)
const Feedback = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/Feedback')
)
const FinanceApplications = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/FinanceApplications'
    )
)
const FinanceDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/FinanceDetailPage')
)
const ForgetPassword = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/ForgotPassword')
)
const InvoiceDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/InvoiceDetailPage')
)
const Invoices = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/Invoices')
)
const Login = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ './components/Login')
)
const MessageSection = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/MessageSection')
)
const MfaRecoveryRequest = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/MfaRecoveryRequest'
    )
)
const MfaRecoveryRequests = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/MfaRecoveryRequests'
    )
)
const MfaRecoveryVerification = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/MfaRecoveryVerification'
    )
)
const MultiFactorSecurity = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/MultiFactorSecurity'
    )
)
const NotificationSettings = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/NotificationSettings'
    )
)
const Notifications = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/Notifications')
)
const OutboundEmailPolicySettingsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/OutboundEmailPolicySettingsPage'
    )
)
const PaymentOperationsReview = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/PaymentOperationsReview'
    )
)
const ProductDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ './components/ProductDetailPage'
    )
)
const ProductPage = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ './components/ProductPage')
)
const PublicQuestions = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/PublicQuestions')
)
const RoleManagement = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ './components/RoleManagement')
)
const RoleManagementAccess = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ './components/RoleManagementAccess'
    )
)
const SalesTax = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/SalesTax')
)
const SalesTaxDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/SalesTaxDetailPage'
    )
)
const Sectors = lazy(
  () => import(/* webpackChunkName: "admin-pages" */ 'components/Sectors')
)
const TaxExemptionApplications = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/TaxExemptionApplications'
    )
)
const TaxExemptionDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/TaxExemptionDetailPage'
    )
)
const TaxJarCustomerPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-pages" */ 'components/TaxJarCustomerPage'
    )
)
const TaxJarCustomers = lazy(
  () =>
    import(/* webpackChunkName: "admin-pages" */ 'components/TaxJarCustomers')
)

function ContactDetailsRoute() {
  const {search} = useLocation()
  const searchParams = new URLSearchParams(search)
  const contactId = searchParams.get('id') || ''
  const isEditMode = searchParams.get('mode') === 'edit'

  return contactId && !isEditMode ? (
    <ContactDetailsPage />
  ) : (
    <CreateNewContact />
  )
}

function App() {
  const isPostgres = usePostgreSQL()
  const {search} = useLocation()
  const {markNotificationAsRead} = useFirebase()
  const notificationId = new URLSearchParams(search).get('notificationId') || ''

  useEffect(() => {
    if (notificationId) {
      if (isPostgres) {
        notificationsApi.markAsRead(notificationId).catch(console.error)
      } else {
        markNotificationAsRead(notificationId)
      }
    }
  }, [isPostgres, markNotificationAsRead, notificationId])

  useEffect(() => {
    registerAppCheckTokenGetter()
  }, [])

  const notificationBannerKey = localStorage.getItem('hideNotificationBanner')
  useEffect(() => {
    setShowNotificationBanner(displayNotificationBanner())
  }, [notificationBannerKey])
  const [showNotificationBanner, setShowNotificationBanner] =
    useState<boolean>()

  const {user, loading} = useSession()
  const isUserLoggedIn = user?.uid
  const principalKey = loading ? 'loading' : user?.uid || 'signed-out'
  const previousPrincipalRef = useRef<string | null>(null)

  useEffect(() => {
    const previousPrincipal = previousPrincipalRef.current
    if (previousPrincipal && previousPrincipal !== user?.uid) {
      clearPendingAdminSubmission(previousPrincipal)
    }
    previousPrincipalRef.current = user?.uid || null
    void setBrowserPushPrincipal(user?.uid || null).catch(() => {})
  }, [user?.uid])
  const notificationBannerCancel = () => {
    localStorage.setItem('hideNotificationBanner', 'true')
    setShowNotificationBanner(false)
  }
  const theme = createTheme({
    palette: {
      primary: {
        main: '#007040',
        contrastText: '#ffffff'
      }
    }
  })

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <AppStateProvider key={principalKey}>
          {isUserLoggedIn && showNotificationBanner && (
            <WebsiteBanner
              showCancelButton
              isNotificationBanner
              cancelButtonClick={notificationBannerCancel}
              bannerText={NOTIFICATION_NOT_GRANTED_MESSAGE}
            />
          )}
          <div>
            <Suspense fallback={<div role="status">Loading page...</div>}>
              <Routes>
                <Route
                  path="/auth_action"
                  element={<AdminEmailVerification />}
                />
                <Route path="/sign-in" element={<Login />} />
                <Route path="/forgot-password" element={<ForgetPassword />} />
                <Route path="/mfa-recovery" element={<MfaRecoveryRequest />} />
                <Route
                  path="/mfa-recovery/verify"
                  element={<MfaRecoveryVerification />}
                />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route
                  path="/notifications/settings"
                  element={<NotificationSettings />}
                />
                <Route
                  path="/notifications/outbound-email-safety"
                  element={<OutboundEmailPolicySettingsPage />}
                />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/deals" element={<DealPage />} />
                <Route
                  path="/deals/deal-details"
                  element={<DealDetailsPage />}
                />
                <Route path="/feedback" element={<Feedback />} />
                <Route
                  path="/finance-applications"
                  element={<FinanceApplications />}
                />
                <Route
                  path="/finance-applications/finance-application-details"
                  element={<FinanceDetailPage />}
                />
                <Route
                  path="/tax-exemption-applications"
                  element={<TaxExemptionApplications />}
                />
                <Route
                  path="/tax-exemption-applications/tax-exemption-application-details"
                  element={<TaxExemptionDetailPage />}
                />
                <Route
                  path="/accounts/single-account-details"
                  element={<CreateNewAccount />}
                />
                <Route path="/ach-agreement" element={<AgreementPage />} />
                <Route path="/msa-agreement" element={<AgreementPage />} />
                <Route path="/listing-agreement" element={<AgreementPage />} />
                <Route
                  path="/ach-agreement/agreement-details"
                  element={<AgreementDetailPage />}
                />
                <Route
                  path="/msa-agreement/agreement-details"
                  element={<AgreementDetailPage />}
                />
                <Route
                  path="/listing-agreement/agreement-details"
                  element={<AgreementDetailPage />}
                />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/security/mfa" element={<MultiFactorSecurity />} />
                <Route
                  path="/mfa-recovery-requests"
                  element={<MfaRecoveryRequests />}
                />
                <Route path="/contacts" element={<ContactPage />} />
                <Route path="/role-management" element={<RoleManagement />} />
                <Route path="/audit-logs" element={<AuditLogPage />} />
                <Route
                  path="/payment-operations-review"
                  element={<PaymentOperationsReview />}
                />
                <Route path="/chats" element={<MessageSection />} />
                <Route path="/messenger" element={<MessageSection />} />
                <Route
                  path="/role-management/role-management-details"
                  element={<RoleManagementAccess />}
                />
                {/* <Route path="/sync" element={<SyncedPage />} /> */}
                <Route
                  path="/contacts/single-contact-details"
                  element={<ContactDetailsRoute />}
                />
                <Route path="/reports" element={<EquipmentReportTable />} />
                <Route
                  path="/reports/settings"
                  element={<EquipmentReportSettingsPage />}
                />
                <Route
                  path="/reports/single-report-detail"
                  element={<EquipmentReportDetailPage />}
                />
                <Route path="/equipment" element={<ProductPage />} />
                <Route path="/categories" element={<Categories />} />
                <Route
                  path="/categories/single-category-details"
                  element={<CreateNewCategory />}
                />
                <Route path="/sectors" element={<Sectors />} />
                <Route
                  path="/sectors/single-sector-details"
                  element={<CreateNewSector />}
                />
                <Route
                  path="/equipment/single-equipment-detail"
                  element={<ProductDetailPage />}
                />
                <Route
                  path="/bank-account-info"
                  element={<BankAccountInformation />}
                />
                <Route path="/invoices" element={<Invoices />} />
                <Route
                  path="/invoices/create-invoice"
                  element={<CreateNewInvoice />}
                />
                <Route
                  path="/invoices/single-invoice-detail"
                  element={<InvoiceDetailPage />}
                />
                <Route path="/sales-tax" element={<SalesTax />} />
                <Route path="/taxjar-customers" element={<TaxJarCustomers />} />
                <Route
                  path="/taxjar-customers/single-taxjar-customer-detail"
                  element={<TaxJarCustomerPage />}
                />
                <Route path="/content-pages" element={<ContentPages />} />
                <Route
                  path="/sales-tax/single-sales-tax-detail"
                  element={<SalesTaxDetailPage />}
                />
                <Route path="/public-questions" element={<PublicQuestions />} />
                <Route path="*" element={<Navigate to="/sign-in" replace />} />
              </Routes>
            </Suspense>
            <ToastContainer closeButton={true} />
          </div>
        </AppStateProvider>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default App
